import { MoreOutlined } from "@ant-design/icons";
import {
  Button,
  ConfigProvider,
  Dropdown,
  InputNumber,
  Menu,
  Pagination,
  Table,
  Tabs,
  message,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router";

import { EditIcon, InactiveIcon, Add, EyeIcon } from "../../icons";
import ConfirmModal from "../../components/ModalLayout/components/ConfirmModal";
import AddUser from "../../components/DrawerLayout/components/AddUser";

import "./style.scss";
import { useMembers } from "../../hooks/useMembersPage";
import { useUpdateMember } from "../../hooks/useUpdateMember";
import { useQueryClient } from "@tanstack/react-query";
import FilterDropdownUser from "../../components/FilterDropDownUser";
import { isPermit } from "../../utils/permissionCheck";
import PermissionDenied from "../OtherPage/PermissionDenied";

// --- TYPE DEFINITIONS ---
export interface User {
  pikitup_member_id: number;
  member_first_name: string;
  member_surname: string;
  member_email_id: string;
  member_mobile_no: string;
  role_name: string;
  member_type_name: string;
  profile_status_flg: number;
  created_date: string;
}

// --- STATUS PILL ---
const StatusPill: React.FC<{ status: number }> = ({ status }) => {
  const label = status === 1 ? "Active" : status === 0 ? "Inactive" : "Deleted";
  return (
    <span className={`status-pill status-${label.toLowerCase()}`}>{label}</span>
  );
};

const UserManagement: React.FC = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { mutate: updateMember } = useUpdateMember();

  const [activeTab, setActiveTab] = useState<"all" | "active" | "inactive">(
    "all"
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const [userFilters, setUserFilters] = useState<any>({});
  const [statusFilter, setStatusFilter] = useState<string | number>("");
  const [searchValue] = useState("");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState<"add" | "edit" | "view">("add");
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalProps, setModalProps] = useState<any>(null);

  // --- Fetch members with pagination and filters ---
  const { data, isLoading } = useMembers(currentPage, pageSize, {
    status: statusFilter,
    ...userFilters,
    search: searchValue,
  });

  const members = data?.members ?? [];

  const total = data?.meta?.total ?? 0;
  const [allTotal, setAllTotal] = useState(total);
  const [allTotalPagination, setAllTotalPagination] = useState(total);

  // Update "All" tab total dynamically whenever API data changes
  useEffect(() => {
    if (activeTab === "all" && currentPage === 1) {
      setAllTotal(total);
      setAllTotalPagination(data?.meta?.total);
    } else if (currentPage === 1) {
      setAllTotalPagination(data?.meta?.total);
    }
  }, [total, activeTab]);

  // --- DELETE USER ---
  const handleDelete = (user: User) => {
    updateMember(
      { action: "delete", member_ids: [user.pikitup_member_id] },
      {
        onSuccess: () => {
          queryClient.invalidateQueries(["members"]);
          message.success("User deleted successfully!");
          setModalOpen(false);
        },
        onError: (err: any) => {
          message.error("Failed to delete user. Please try again.");
          console.error(err);
        },
      }
    );
  };

  // --- DROPDOWN MENU ---
  const ActionMenu = (record: User) => {
    const canEdit = isPermit(3, "edit");
    const canDelete = isPermit(3, "delete");

    return (
      <Menu
        onClick={({ key }) => {
          if (key === "view") {
            setDrawerMode("view");
            setDrawerOpen(true);
            setSelectedUser(record);
          }
          if (key === "edit" && canEdit) {
            setDrawerMode("edit");
            setDrawerOpen(true);
            setSelectedUser(record);
          }
          if (key === "delete" && canDelete) {
            setModalProps({
              title: "Confirm Delete",
              message: `Are you sure you want to delete ${record.member_first_name} ${record.member_surname}?`,
              onConfirm: () => handleDelete(record),
              confirmText: "Delete",
            });
            setModalOpen(true);
          }
        }}
        items={[
          ...(canEdit
            ? [{ icon: <EditIcon />, key: "edit", label: "Edit" }]
            : []),
          { icon: <EyeIcon />, key: "view", label: "View" },
          ...(canDelete
            ? [
                {
                  icon: <InactiveIcon />,
                  key: "delete",
                  danger: true,
                  label: "Remove",
                },
              ]
            : []),
        ]}
      />
    );
  };

  // --- TABLE COLUMNS ---
  const columns: ColumnsType<User> = [
    {
      title: "Name",
      key: "name",
      width: 200,
      fixed: "left",
      render: (_, record) => (
        <span
          className="cursor-pointer"
          onClick={() => {
            setDrawerMode("view");
            setDrawerOpen(true);
            setSelectedUser(record);
          }}
        >
          {record.member_first_name} {record.member_surname}
        </span>
      ),
    },
    {
      title: "Email",
      dataIndex: "member_email_id",
      key: "member_email_id",
      width: 220,
      fixed: "left",
      ellipsis: true,
    },
    {
      title: "Mobile number",
      dataIndex: "member_mobile_no",
      key: "member_mobile_no",
      width: 200,
      fixed: "left",
      ellipsis: true,
    },
    {
      title: "Role",
      dataIndex: "role_name",
      key: "role_name",
      width: 200,
    },
    {
      title: "Depot name",
      dataIndex: "depots", // or whatever the actual key is in the row data
      key: "depots",
      width: 250,
      render: (_: any, record: Unit) => {
        const depots = record.depot_ids;

        if (!Array.isArray(depots) || depots.length === 0) return "-";

        const first = depots[0]?.depot_name;
        const rest = depots.slice(1);

        return (
          <span>
            {first}
            {rest.length > 0 && <span className="count"> +{rest.length}</span>}
          </span>
        );
      },
    },
    {
      title: "Member type",
      dataIndex: "member_type_name",
      key: "member_type_name",
      width: 200,
      ellipsis: true,
    },
    {
      title: "Status",
      dataIndex: "profile_status_flg",
      key: "profile_status_flg",
      width: 150,
      render: (status: number) => <StatusPill status={status} />,
    },
    {
      title: "Created on",
      dataIndex: "created_date",
      key: "created_date",
      width: 150,
      ellipsis: true,
    },
    {
      title: "",
      key: "actions",
      width: 50,
      fixed: "right",
      ellipsis: true,
      render: (_text, record) => (
        <Dropdown overlay={ActionMenu(record)} trigger={["click"]}>
          <Button icon={<MoreOutlined />} type="text" />
        </Dropdown>
      ),
    },
  ];

  const tableHeight = window.innerHeight - 380;

  return (
    <div className="dashboard">
      {isPermit(3, "view") ? (
        <>
          <div className="unit-header">
            <div className="flex items-center gap-4">
              <h2 className="unit-title">User Management</h2>
              <ConfigProvider
                theme={{
                  components: {
                    Tabs: {
                      inkBarColor: "#1677ff",
                      itemSelectedColor: "#1677ff",
                      itemHoverColor: "#4096ff",
                      itemColor: "rgba(0,0,0,0.88)",
                    },
                  },
                }}
              >
                <Tabs
                  activeKey={activeTab}
                  items={[
                    { key: "all", label: `All (${allTotal})` },
                    { key: "active", label: `Active` },
                    { key: "inactive", label: `Inactive` },
                  ]}
                  onChange={(key) => {
                    setActiveTab(key as any);
                    setCurrentPage(1);
                    if (key === "all") setStatusFilter("");
                    if (key === "active") setStatusFilter(1);
                    if (key === "inactive") setStatusFilter(0);
                  }}
                  className="status-tabs"
                />
              </ConfigProvider>
            </div>

            {isPermit(3, "create") && (
              <button
                className="add-unit-btn"
                onClick={() => {
                  setDrawerOpen(true);
                  setDrawerMode("add");
                  setSelectedUser(null);
                }}
              >
                <span className="mr-1">
                  <Add />
                </span>
                ADD USER
              </button>
            )}
          </div>

          {/* Filters */}
          <div className="table-controls">
            {isPermit(5, "view") && (
              <div className="flex items-center gap-3">
                <button
                  className="manage-btn"
                  onClick={() => navigate("/user-role")}
                >
                  Manage Roles
                </button>
                <FilterDropdownUser
                  activeTab={activeTab}
                  onChange={(apiParams) => {
                    setUserFilters(apiParams);
                    setCurrentPage(1);
                  }}
                />
              </div>
            )}
          </div>

          {/* Table */}
          <div className="table-wrapper">
            <Table
              rowKey="pikitup_member_id"
              columns={columns}
              dataSource={members}
              loading={isLoading}
              scroll={{ x: 1000, y: tableHeight }}
              pagination={false}
            />
          </div>

          {/* Pagination */}
          <div className="pagination-wrapper">
            <div className="pagination-info">
              Page
              <InputNumber
                controls={false}
                min={1}
                max={
                  allTotalPagination
                    ? Math.ceil(allTotalPagination / pageSize)
                    : allTotalPagination
                }
                value={currentPage}
                onChange={(val) => setCurrentPage(val || 1)}
                style={{ width: 60 }}
              />
              <span>
                {" "}
                of{" "}
                {allTotalPagination
                  ? Math.ceil(allTotalPagination / pageSize)
                  : 1}
              </span>
            </div>

            <Pagination
              current={currentPage}
              pageSize={pageSize}
              total={allTotalPagination}
              onChange={(page) => setCurrentPage(page)}
              showSizeChanger={false}
            />
          </div>

          {/* Drawer */}
          <AddUser
            open={drawerOpen}
            setOpen={setDrawerOpen}
            title={
              drawerMode === "view"
                ? "User Details"
                : drawerMode === "edit"
                ? "Edit User"
                : "Add User"
            }
            user={selectedUser}
            mode={drawerMode}
            onEditClick={() => setDrawerMode("edit")}
          />

          {/* Confirm Modal */}
          {modalOpen && (
            <ConfirmModal
              open={modalOpen}
              title={modalProps?.title ?? ""}
              message={modalProps?.message}
              confirmText={modalProps?.confirmText}
              onClose={() => {
                setModalOpen(false);
                setModalProps(null);
              }}
              onConfirm={() => {
                modalProps?.onConfirm?.();
                setModalOpen(false);
                setModalProps(null);
              }}
            />
          )}
        </>
      ) : (
        <PermissionDenied />
      )}
    </div>
  );
};

export default UserManagement;
